import React, { useState, useEffect } from 'react';
import { Button, Modal, Table, Spinner, Form, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faFileAlt, faClock, faSearch, faFileCsv, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const RequestPage = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://agpipefitting.org.et/api/requests');
      setRequests(response.data);
      setFilteredRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    const results = requests.filter(request =>
      Object.values(request).some(value =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredRequests(results);
  }, [searchTerm, requests]);

  const handleViewDetails = (request) => {
    setCurrentRequest(request);
    setShowDetailsModal(true);
  };

  const handleDeleteRequest = async (id) => {
    try {
      await axios.delete(`https://agpipefitting.org.et/api/requests/${id}`);
      fetchRequests();
    } catch (error) {
      console.error('Error deleting request:', error);
      alert('There was an error deleting the request.');
    }
  };

  const handleExportCSV = () => {
    if (filteredRequests.length === 0) {
      alert('No data to export');
      return;
    }

    const csvHeaders = [
      'ID',
      'Client Name',
      'Email',
      'Phone',
      'Product Type',
      'Priority',
      'Quantity',
      'Unit Price (ETB)',
      'Total Value (ETB)',
      'Delivery Address',
      'Due Date',
      'TIN Number',
      'Created Date'
    ];

    const csvRows = filteredRequests.map(request => [
      request.id,
      `"${request.name.replace(/"/g, '""')}"`,
      `"${request.email}"`,
      `"${request.phone}"`,
      `"${request.productType}"`,
      request.priority,
      request.quantity,
      request.price,
      request.quantity * request.price,
      `"${request.address.replace(/"/g, '""')}"`,
      new Date(request.dueDate).toLocaleDateString(),
      request.tinNo || 'N/A',
      new Date(request.createdAt).toLocaleDateString()
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'requests.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = () => {
    if (filteredRequests.length === 0) {
      alert('No data to export');
      return;
    }

    const doc = new jsPDF();
    const title = 'Requests Report - ' + new Date().toLocaleDateString();
    const headers = [
      ['ID', 'Client Name', 'Product Type', 'Priority', 'Quantity', 'Unit Price', 'Total', 'Created Date']
    ];
    
    const data = filteredRequests.map(request => [
      request.id,
      request.name,
      request.productType,
      request.priority,
      request.quantity,
      `ETB ${request.price}`,
      `ETB ${request.quantity * request.price}`,
      new Date(request.dueDate).toLocaleDateString()
    ]);

    doc.setFontSize(16);
    doc.text(title, 14, 15);
    doc.setFontSize(10);
    
    doc.autoTable({
      startY: 25,
      head: headers,
      body: data,
      theme: 'grid',
      styles: { fontSize: 9 },
      headStyles: { 
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 35 },
        2: { cellWidth: 30 },
        3: { cellWidth: 20 },
        4: { cellWidth: 20 },
        5: { cellWidth: 25 },
        6: { cellWidth: 25 },
        7: { cellWidth: 30 }
      }
    });

    doc.save('requests-report.pdf');
  };

  return (
    <div className="container-fluid mt-4 px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold text-primary">Request Management</h2>
      </div>

      <div className="card shadow-sm">
        <div className="card-header bg-white border-bottom-0">
          <Row className="align-items-center g-3">
            <Col md={4}>
              <h4 className="mb-0">Active Requests ({filteredRequests.length})</h4>
            </Col>
            <Col md={4}>
              <Form.Control 
                type="search" 
                placeholder="Search requests..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="rounded-pill"
              />
            </Col>
            <Col md={4} className="d-flex justify-content-end gap-2">
              <Button 
                variant="outline-success" 
                onClick={handleExportCSV}
                disabled={filteredRequests.length === 0}
              >
                <FontAwesomeIcon icon={faFileCsv} className="me-2" />
                Export CSV
              </Button>
              <Button 
                variant="outline-danger" 
                onClick={handleExportPDF}
                disabled={filteredRequests.length === 0}
              >
                <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                Export PDF
              </Button>
            </Col>
          </Row>
        </div>
        
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading requests...</p>
            </div>
          ) : (
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>#</th>
                  <th>Client</th>
                  <th>Contact</th>
                  <th>Product</th>
                  <th>Priority</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((request, index) => (
                  <tr key={request.id}>
                    <td className="text-muted">{index + 1}</td>
                    <td>
                      <div className="fw-semibold">{request.name}</div>
                      <small className="text-muted">{request.email}</small>
                    </td>
                    <td>{request.phone}</td>
                    <td>
                      <span className="badge bg-primary-light text-primary">
                        {request.productType}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${
                        request.priority === 'High' ? 'bg-danger' : 
                        request.priority === 'Medium' ? 'bg-warning text-dark' : 'bg-secondary'
                      }`}>
                        {request.priority}
                      </span>
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewDetails(request)}
                        className="me-2"
                      >
                        <FontAwesomeIcon icon={faEye} className="me-1" />
                        Details
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteRequest(request.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      <Modal 
        show={showDetailsModal} 
        onHide={() => setShowDetailsModal(false)}
        centered
        size="lg"
      >
        <Modal.Header 
          closeButton 
          className="bg-primary text-white"
        >
          <Modal.Title>
            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
            Request Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#f8f9fa' }}>
          {currentRequest && (
            <div className="container-fluid">
              <div className="mb-4">
                <h6 className="text-muted text-uppercase mb-3">Client Information</h6>
                <Row>
                  <Col md={4} className="mb-3">
                    <label className="text-secondary small">Full Name</label>
                    <p className="fw-semibold mb-0">{currentRequest.name}</p>
                  </Col>
                  <Col md={4} className="mb-3">
                    <label className="text-secondary small">Phone Number</label>
                    <p className="fw-semibold mb-0">{currentRequest.phone}</p>
                  </Col>
                  <Col md={4} className="mb-3">
                    <label className="text-secondary small">Email Address</label>
                    <p className="fw-semibold mb-0">{currentRequest.email}</p>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <h6 className="text-muted text-uppercase mb-3">Order Specifications</h6>
                <Row>
                  <Col md={3} className="mb-3">
                    <label className="text-secondary small">Product Type</label>
                    <p className="fw-semibold mb-0">{currentRequest.productType}</p>
                  </Col>
                  <Col md={3} className="mb-3">
                    <label className="text-secondary small">Quantity</label>
                    <p className="fw-semibold mb-0">{currentRequest.quantity}</p>
                  </Col>
                  <Col md={3} className="mb-3">
                    <label className="text-secondary small">Unit Price</label>
                    <p className="fw-semibold mb-0">ETB {currentRequest.price}</p>
                  </Col>
                  <Col md={3} className="mb-3">
                    <label className="text-secondary small">Total Value</label>
                    <p className="fw-semibold mb-0">
                      ETB {(currentRequest.quantity * currentRequest.price).toLocaleString()}
                    </p>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <h6 className="text-muted text-uppercase mb-3">Additional Details</h6>
                <Row>
                  <Col md={6} className="mb-3">
                    <label className="text-secondary small">Delivery Address</label>
                    <p className="fw-semibold mb-0">{currentRequest.address}</p>
                  </Col>
                  <Col md={3} className="mb-3">
                    <label className="text-secondary small">Due Date</label>
                    <p className="fw-semibold mb-0">
                      {new Date(currentRequest.dueDate).toLocaleDateString()}
                    </p>
                  </Col>
                  <Col md={3} className="mb-3">
                    <label className="text-secondary small">TIN Number</label>
                    <p className="fw-semibold mb-0">{currentRequest.tinNo || 'N/A'}</p>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div className="text-muted small">
            <FontAwesomeIcon icon={faClock} className="me-1" />
            Created: {new Date(currentRequest?.createdAt).toLocaleDateString()}
          </div>
          <Button 
            variant="secondary" 
            onClick={() => setShowDetailsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RequestPage;